import React, {useEffect, useMemo, useRef} from "react";
import * as THREE from "three";
import {useFrame, useThree} from "@react-three/fiber";
import {observer_mgl_r3} from "../../Utils/ThreeJS/General.js";
import {DMap} from "../../Server/DB/maps/@Map.js";
import {MapState} from "../../Store/main/maps/@MapState.js";
import {mapZIndexes} from "../../UI/Maps/MapUI/MapSurface/MapZIndexes.js";
import {
	terrainDataVersion,
	terrainPreparedData_current,
} from "./TerrainRenderer.js";
import type {PreparedTerrainDataV3} from "./TerrainWorker.js";
import {CellType, NEU_E, NEU_N, NEU_W, NEU_S} from "./Poisson/AMRGrid.js";
import {readLeaf, SNAPSHOT_DOUBLES_PER_LEAF, type CellSnapshot} from "./Poisson/CellSnapshot.js";

/** V3.1 ConstraintCellOverlay (plan §2.3/§4.3/§6.5). Renders rasterized AMR constraint cells
 *  by type: Dirichlet green, SoftPin amber (`k·(H−target)`), Neumann cyan ticks, Barrier magenta,
 *  Free no fill; AMR level = fill intensity. Data: `PreparedTerrainDataV3` `CellSnapshot`s (opt-in via `overlay_showConstraintCells`). */
export const IsolineSamplePointsOverlay = observer_mgl_r3((props: {map: DMap, mapState: MapState})=>{
	const {map, mapState} = props;
	const settings = mapState.settings;
	const terrain = settings.terrain;
	// Observe the prepared-data version so MobX re-renders this component on each refresh.
	const _version = terrainDataVersion.get();
	void _version; // referenced for reactivity; the build loop reads the map directly.

	// Off-gate: render nothing when the overlay toggle is off (the worker also doesn't
	// build snapshots in that case — see TerrainWorker.prepareTerrainV3).
	if (!terrain.overlay_showConstraintCells) return null;
	// Need at least one prepared mod with a snapshot.
	const prepareds = [...terrainPreparedData_current.values()].filter(p=>p.macroSnapshot || p.workingSnapshot);
	if (prepareds.length === 0) return null;

	return <OverlayInner prepareds={prepareds}/>;
});

/** Inner component that owns the Three.js geometry + the per-frame label-position loop. */
const OverlayInner = observer_mgl_r3((props: {prepareds: PreparedTerrainDataV3[]})=>{
	const {prepareds} = props;
	const {camera} = useThree();
	const groupRef = useRef<THREE.Group>(null);

	// Rebuild when `prepareds` identity changes (re-created each `terrainDataVersion` bump);
	// snapSig folds in snapshot cell-counts so a re-dispatch with a new snapshot re-builds too.
	const snapSig = prepareds.map(p=>`${p.cacheKey}|${p.macroSnapshot?.leavesCount ?? 0}|${p.workingSnapshot?.leavesCount ?? 0}`).join("~");
	const built = useMemo(()=>buildOverlayGeometry(prepareds), [prepareds, snapSig]);
	const {cellsGeometry, barriersGeometry, neumannGeometry, labels} = built;

	// Dispose geometry on unmount / rebuild.
	useEffect(()=>{
		const geos = [cellsGeometry, barriersGeometry, neumannGeometry];
		return ()=>{
			for (const g of geos) g.dispose();
			for (const l of labels) {
				(l.material as THREE.SpriteMaterial).map?.dispose();
				l.material.dispose();
			}
		};
	}, [cellsGeometry, barriersGeometry, neumannGeometry, labels]);

	// Per-frame: re-position labels to the camera-visible world bbox corner.
	useFrame(()=>{
		const group = groupRef.current;
		if (!group) return;
		const cam = camera as THREE.OrthographicCamera;
		const halfW = (cam.right - cam.left) / (2 * cam.zoom);
		const halfH = (cam.top - cam.bottom) / (2 * cam.zoom);
		const baseX = cam.position.x - halfW + halfW * 0.05;
		let baseY = cam.position.y + halfH - halfH * 0.05;
		for (let i = 0; i < labels.length; i++) {
			labels[i].position.set(baseX, baseY, mapZIndexes.isolineSamplePoints + 0.1);
			baseY -= halfH * 0.06;
		}
	});

	return (
		<group ref={groupRef}>
			<lineSegments>
				<primitive object={cellsGeometry} attach="geometry"/>
				<lineBasicMaterial vertexColors transparent depthTest={false}/>
			</lineSegments>
			<lineSegments>
				<primitive object={barriersGeometry} attach="geometry"/>
				<lineBasicMaterial vertexColors transparent depthTest={false}/>
			</lineSegments>
			<lineSegments>
				<primitive object={neumannGeometry} attach="geometry"/>
				<lineBasicMaterial vertexColors transparent depthTest={false}/>
			</lineSegments>
			{labels.map((l, i)=>(
				<primitive key={i} object={l}/>
			))}
		</group>
	);
});

/** Colour palette for each constraint type. */
const COLOR_DIRICHLET = new THREE.Color(0.20, 0.85, 0.35);   // green
const COLOR_NEUMANN = new THREE.Color(0.30, 0.80, 0.95);    // cyan (edge ticks)
const COLOR_BARRIER = new THREE.Color(0.95, 0.35, 0.85);    // magenta
// **2026-07-30 — SoftPin** (penalty / spring pin): a yellow/orange outline distinguishes a
// soft-pinned rim cell from a hard Dirichlet one at a glance (both are "pinned" but the
// soft pin leaves the gradient continuous, so the colour difference matters).
const COLOR_SOFTPIN = new THREE.Color(0.95, 0.80, 0.20);    // amber

/** Per-solve label sprite factory. Returns a Sprite positioned by the per-frame loop. */
function makeLabel(text: string): THREE.Sprite {
	const canvas = document.createElement("canvas");
	canvas.width = 512; canvas.height = 96;
	const ctx = canvas.getContext("2d")!;
	ctx.fillStyle = "rgba(0,0,0,0.55)";
	ctx.fillRect(0, 0, canvas.width, canvas.height);
	ctx.fillStyle = "white";
	ctx.font = "bold 40px sans-serif";
	ctx.textBaseline = "middle";
	ctx.fillText(text, 12, canvas.height / 2);
	const tex = new THREE.CanvasTexture(canvas);
	tex.needsUpdate = true;
	const mat = new THREE.SpriteMaterial({map: tex, transparent: true, depthTest: false});
	const sprite = new THREE.Sprite(mat);
	// Scale roughly proportional to a small world distance; the per-frame loop keeps the
	// sprite anchored to the top-left of the camera rect regardless of zoom.
	sprite.scale.set(80, 15, 1);
	sprite.renderOrder = 10;
	return sprite;
}

/** Push a 4-segment rectangle outline (8 verts) into the given position + colour buffers. */
function pushRect(
	pos: Float32Array, col: Float32Array, fillStart: number,
	minX: number, minY: number, maxX: number, maxY: number, z: number,
	c: THREE.Color, intensity: number,
): number {
	const pi = fillStart * 3;
	const ci = fillStart * 3;
	const r = c.r * intensity, g = c.g * intensity, b = c.b * intensity;
	// 4 segments (BL→BR→TR→TL→BL): 8 vertices.
	const pts: Array<[number, number]> = [
		[minX, minY], [maxX, minY],
		[maxX, minY], [maxX, maxY],
		[maxX, maxY], [minX, maxY],
		[minX, maxY], [minX, minY],
	];
	for (let k = 0; k < pts.length; k++) {
		const idx = pi + k * 3;
		pos[idx] = pts[k][0]; pos[idx + 1] = pts[k][1]; pos[idx + 2] = z;
		const cidx = ci + k * 3;
		col[cidx] = r; col[cidx + 1] = g; col[cidx + 2] = b;
	}
	return fillStart + 8;
}

/** Push a Neumann tick along a pinned face: a short centred segment on the face. */
function pushTick(
	pos: Float32Array, col: Float32Array, fillStart: number,
	minX: number, minY: number, maxX: number, maxY: number, z: number, face: number,
): number {
	const pi = fillStart * 3;
	const ci = fillStart * 3;
	const r = COLOR_NEUMANN.r, g = COLOR_NEUMANN.g, b = COLOR_NEUMANN.b;
	const midX = (minX + maxX) * 0.5, midY = (minY + maxY) * 0.5;
	let a: [number, number], c2: [number, number];
	if (face === NEU_E) { a = [maxX, midY - (maxY - minY) * 0.25]; c2 = [maxX, midY + (maxY - minY) * 0.25]; }
	else if (face === NEU_W) { a = [minX, midY - (maxY - minY) * 0.25]; c2 = [minX, midY + (maxY - minY) * 0.25]; }
	else if (face === NEU_N) { a = [minX + (maxX - minX) * 0.25, maxY]; c2 = [maxX - (maxX - minX) * 0.25, maxY]; }
	else { a = [minX + (maxX - minX) * 0.25, minY]; c2 = [maxX - (maxX - minX) * 0.25, minY]; }
	pos[pi] = a[0]; pos[pi + 1] = a[1]; pos[pi + 2] = z;
	col[ci] = r; col[ci + 1] = g; col[ci + 2] = b;
	pos[pi + 3] = c2[0]; pos[pi + 4] = c2[1]; pos[pi + 5] = z;
	col[ci + 3] = r; col[ci + 4] = g; col[ci + 5] = b;
	return fillStart + 2;
}

const NEU_BITS = [NEU_E, NEU_N, NEU_W, NEU_S];

/** AMR level → intensity: coarser cells (low level) brighter so they read above the dense
 *  fine interior. Log-ish scaling so a 16-level range doesn't crush fine cells to invisible. */
function levelIntensity(level: number, maxLevel: number): number {
	const t = maxLevel > 0 ? Math.min(1, level / maxLevel) : 0;
	return 0.85 - 0.55 * t;
}

/** Build three LineSegments geometries (cells / barriers / Neumann edge ticks) + labels
 *  from the snapshots attached to all prepared mods. The cells layout is bounded by the
 *  snapshots' leaf count (camera-window-bounded per §7.1), so this build is cheap. */
function buildOverlayGeometry(prepareds: PreparedTerrainDataV3[]): {
	cellsGeometry: THREE.BufferGeometry;
	barriersGeometry: THREE.BufferGeometry;
	neumannGeometry: THREE.BufferGeometry;
	labels: THREE.Sprite[];
} {
	// Pre-count leaf slots across both grids (each leaf contributes up to 4 outline segments
	// of 2 vertices each = 8 verts; Neumann ticks each add 2 verts per set bit).
	let cellVerts = 0, barrierVerts = 0, neumannVerts = 0;
	for (const p of prepareds) {
		for (const snap of [p.macroSnapshot, p.workingSnapshot]) {
			if (!snap) continue;
			for (let i = 0; i < snap.leavesCount; i++) {
				const off = i * SNAPSHOT_DOUBLES_PER_LEAF;
				const type = snap.cells[off + 4] as CellType;
				const neuFaces = snap.cells[off + 6];
				if (type === CellType.Barrier) barrierVerts += 8;
				else if (type === CellType.Dirichlet || type === CellType.SoftPin) cellVerts += 8;
				let bits = neuFaces;
				while (bits) { if (bits & 1) neumannVerts += 2; bits >>= 1; }
			}
		}
	}

	const cellsPos = new Float32Array(cellVerts * 3);
	const cellsCol = new Float32Array(cellVerts * 3);
	const barPos = new Float32Array(barrierVerts * 3);
	const barCol = new Float32Array(barrierVerts * 3);
	const neuPos = new Float32Array(neumannVerts * 3);
	const neuCol = new Float32Array(neumannVerts * 3);
	let cF = 0, bF = 0, nF = 0;
	const labels: THREE.Sprite[] = [];

	let modIndex = 0;
	for (const p of prepareds) {
		// Outer "macro" snapshot first, then working — drawn in z-stacked layers so finer cells
		// draw on top (z+0.5 per grid kind to keep their outlines from z-fighting each other).
		const grids: Array<{snap: CellSnapshot | undefined, z: number}> = [
			{snap: p.macroSnapshot, z: mapZIndexes.isolineSamplePoints + 0.0},
			{snap: p.workingSnapshot, z: mapZIndexes.isolineSamplePoints + 0.5},
		];
		// Per-grid max level (so intensity scaling is local).
		let maxLevel = 0;
		for (const g of grids) {
			if (!g.snap) continue;
			for (let i = 0; i < g.snap.leavesCount; i++) {
				const lvl = g.snap.cells[i * SNAPSHOT_DOUBLES_PER_LEAF + 5];
				if (lvl > maxLevel) maxLevel = lvl;
			}
		}
		for (const g of grids) {
			const snap = g.snap;
			if (!snap) continue;
			for (let i = 0; i < snap.leavesCount; i++) {
				const leaf = readLeaf(snap, i);
				const intensity = levelIntensity(leaf.level, maxLevel);
				if (leaf.type === CellType.Barrier) {
					bF = pushRect(barPos, barCol, bF, leaf.minX, leaf.minY, leaf.maxX, leaf.maxY, g.z, COLOR_BARRIER, intensity);
				} else if (leaf.type === CellType.SoftPin) {
					// Soft-pin rim cell: amber outline (distinct from the green Dirichlet fill — the
					// gradient stays continuous across a SoftPin cell).
					cF = pushRect(cellsPos, cellsCol, cF, leaf.minX, leaf.minY, leaf.maxX, leaf.maxY, g.z, COLOR_SOFTPIN, intensity);
					for (const bit of NEU_BITS) {
						if (leaf.neumannFaces & bit) nF = pushTick(neuPos, neuCol, nF, leaf.minX, leaf.minY, leaf.maxX, leaf.maxY, g.z + 0.05, bit);
					}
				} else if (leaf.type === CellType.Dirichlet) {
					cF = pushRect(cellsPos, cellsCol, cF, leaf.minX, leaf.minY, leaf.maxX, leaf.maxY, g.z, COLOR_DIRICHLET, intensity);
					// Neumann ticks on a Dirichlet-and-Neumann cell (closed-ring rim).
					for (const bit of NEU_BITS) {
						if (leaf.neumannFaces & bit) nF = pushTick(neuPos, neuCol, nF, leaf.minX, leaf.minY, leaf.maxX, leaf.maxY, g.z + 0.05, bit);
					}
				} else if (leaf.neumannFaces !== 0) {
					// Pure Free cell with Neumann faces (open-curve carrier).
					for (const bit of NEU_BITS) {
						if (leaf.neumannFaces & bit) nF = pushTick(neuPos, neuCol, nF, leaf.minX, leaf.minY, leaf.maxX, leaf.maxY, g.z + 0.05, bit);
					}
				}
			}
		}
		// One label per prepared mod // hidden atm, too distracting at close zoom
		/*const macroRes = p.macroSolveStats.finalResidual.toExponential(2);
		const workRes = p.workingSolveStats.finalResidual.toExponential(2);
		labels.push(makeLabel(
			`#${modIndex + 1} macro=${p.macroSolveStats.features}f/${p.macroSolveStats.cells}c r=${macroRes} | work=${p.workingSolveStats.features}f/${p.workingSolveStats.cells}c r=${workRes}`,
		));*/
		modIndex++;
	}

	const cellsGeometry = new THREE.BufferGeometry();
	cellsGeometry.setAttribute("position", new THREE.BufferAttribute(cellsPos, 3));
	cellsGeometry.setAttribute("color", new THREE.BufferAttribute(cellsCol, 3));

	const barriersGeometry = new THREE.BufferGeometry();
	barriersGeometry.setAttribute("position", new THREE.BufferAttribute(barPos, 3));
	barriersGeometry.setAttribute("color", new THREE.BufferAttribute(barCol, 3));

	const neumannGeometry = new THREE.BufferGeometry();
	neumannGeometry.setAttribute("position", new THREE.BufferAttribute(neuPos, 3));
	neumannGeometry.setAttribute("color", new THREE.BufferAttribute(neuCol, 3));

	return {cellsGeometry, barriersGeometry, neumannGeometry, labels};
}
